import type { FeatureFlagsResponse } from '~/hooks/queries/use-feature-flags';
import type { ProBuilderContextReading } from '~/hooks/queries/use-pro-builder-session';
import { PRO_BUILDER_FLAG } from '~/lib/feature-flag-names';
import { contextPercent, type ContextUsage } from '~/features/coding/coding-events';

/**
 * The Pro Builder window's pure decisions (FRONT-3003), kept out of the
 * components so each has a sweep test. The top rule behind all of them: a
 * running Claude Code survives hiding the window, unmounting it and a page
 * reload (they only DETACH; the next open attaches). Only an explicit Restart
 * ends it, besides the shell exiting, the daemon's 30-minute no-output reaper,
 * the sandbox going away, or the server ending it when the organization switch
 * goes off. Reconnect re-opens and attaches when the shell is still alive.
 */

/** The deep link that opens the window and then redirects home. */
export const PRO_BUILDER_DEEP_LINK_PATH = '/pro-builder';

/**
 * What the last flags DATA says about Pro Builder: `true` / `false`, or
 * `undefined` when there is no data at all. Unlike the sidebar's
 * `resolveFeatureFlag` (error or loading => fallback false, which governs
 * SHOWING a doorway), this ignores `isError`: TanStack keeps `data` through a
 * failed background refetch, and this answer governs UNMOUNTING the terminal.
 */
export function proBuilderFlagFromData(data: FeatureFlagsResponse | undefined): boolean | undefined {
  if (!data) return undefined;
  if (!data.configured) return true;
  const flag = data.flags.find((f) => f.name === PRO_BUILDER_FLAG);
  return flag ? flag.enabled : false;
}

/**
 * Mount rule for the app shell: never before the first open request; then
 * mounted unless the data POSITIVELY says the flag is off. With no data
 * (still loading) the host stays mounted only if it already was.
 */
export function shouldMountProBuilderWindow(
  flagFromData: boolean | undefined,
  everOpened: boolean,
  wasMounted: boolean,
): boolean {
  if (!everOpened) return false;
  if (flagFromData === undefined) return wasMounted;
  return flagFromData;
}

/**
 * The settings-family modals are ROUTES, so they close by navigating
 * (`useModalClose`). They close themselves when Pro Builder opens over them
 * (`useCloseOnProBuilderOpen` in `settings-modal-shell.tsx`), and that
 * navigation must not hide the window that caused it.
 */
export const SELF_CLOSING_MODAL_PATHS: readonly string[] = ['/settings', '/system-settings'];

/**
 * Hide on any location change while open, with three exceptions:
 *  - arriving AT the deep link (`/pro-builder`): its route has just opened
 *    the window, and its own effect runs before this check sees the change;
 *  - the deep link's `/pro-builder` -> `/` redirect that follows;
 *  - leaving a settings-family modal route for a page that is not one: that
 *    is the modal closing itself because the window opened over it. Pro
 *    Builder cannot be open over such a route any other way — navigating TO
 *    one hides the window.
 * Pathname only: a query string change on the same page is not leaving it.
 */
export function shouldHideOnNavigation(previousPath: string, nextPath: string, open: boolean): boolean {
  if (!open || previousPath === nextPath) return false;
  if (nextPath === PRO_BUILDER_DEEP_LINK_PATH) return false;
  if (previousPath === PRO_BUILDER_DEEP_LINK_PATH && nextPath === '/') return false;
  if (SELF_CLOSING_MODAL_PATHS.includes(previousPath) && !SELF_CLOSING_MODAL_PATHS.includes(nextPath)) return false;
  return true;
}

/**
 * Where a keydown landed, relative to the window: inside xterm (Claude Code's
 * own keys), elsewhere inside the window, on the page body (focus fell there
 * after a click on non-focusable chrome), or somewhere else entirely — another
 * layer above the window, such as the command palette.
 */
export type EscapeZone = 'terminal' | 'window' | 'body' | 'elsewhere';

export function escapeZone(
  target: { closest?: (selector: string) => unknown } | null,
  windowRoot: { contains: (node: never) => boolean } | null,
  body: unknown,
  documentElement: unknown,
): EscapeZone {
  if (!target || target === body || target === documentElement) return 'body';
  if (target.closest?.('.xterm')) return 'terminal';
  if (windowRoot?.contains(target as never)) return 'window';
  return 'elsewhere';
}

/**
 * A visible window swallows Escape from its own chrome and from the page body,
 * so it never reaches page handlers underneath (Settings' Esc, list shortcuts).
 * xterm keeps its Escape, and a layer above the window keeps its own.
 */
export function shouldSwallowEscape(key: string, open: boolean, zone: EscapeZone): boolean {
  return open && key === 'Escape' && (zone === 'window' || zone === 'body');
}

/** Where focus sits when the window hides. */
export type FocusAtHide = 'nowhere' | 'inside-window' | 'elsewhere';

/**
 * Whether hiding hands focus back to what opened the window. Only when the
 * open came from the keyboard — a pointer click needs no focus back, and a
 * programmatic focus opens the opener's hover tooltip (the sidebar row's),
 * which then eats the next Escape. And only when focus has nowhere better to
 * be: on the body, or still inside the now-hidden window. Focus the person
 * already moved elsewhere (the surface that caused the hide) is left alone,
 * and nothing is ever blurred.
 */
export function focusReturnTarget(
  opener: { isConnected: boolean } | null,
  openedFromKeyboard: boolean,
  focus: FocusAtHide,
): 'opener' | 'leave' {
  if (!opener?.isConnected || !openedFromKeyboard) return 'leave';
  return focus === 'elsewhere' ? 'leave' : 'opener';
}

/**
 * FRONT-3040: whether the window's visibility just changed in a way that means
 * the person has looked at the session's state: it became visible (they are
 * looking now) or it was hidden after having been visible (what they saw while
 * it was open is now seen). Exactly the two edges, so a steady state sends
 * nothing and each transition sends once.
 */
export function shouldMarkSeen(previousOpen: boolean, open: boolean): boolean {
  return previousOpen !== open;
}

/** The header's Save disc (it publishes the bound app): only where the terminal lives, and only for a person who may build apps. */
export function showPublishButton(publishable: boolean, canBuildApps: boolean): boolean {
  return publishable && canBuildApps;
}

/** The publish details dialog is open only while the window that opened it is showing (a hidden window stays mounted). */
export function publishDialogOpen(requested: boolean, windowOpen: boolean): boolean {
  return requested && windowOpen;
}

/**
 * FRONT-3077: the header's context meter input, from the session read's
 * `context`. The same shape the guided Builder's meter takes, with the same
 * rounding (`contextPercent`), so the shared `ContextMeter` renders it
 * unchanged. Null — no meter — when there is no reading yet (or an older
 * server sends none), or it carries no tokens; an unknown or zero window
 * shows the raw count instead of a percentage, exactly as the Builder does.
 */
export function proBuilderContextUsage(context: ProBuilderContextReading | null | undefined): ContextUsage | null {
  if (!context || !(context.tokens > 0)) return null;
  const windowTokens = typeof context.windowTokens === 'number' && context.windowTokens > 0 ? context.windowTokens : null;
  return {
    contextTokens: context.tokens,
    windowTokens,
    percent: windowTokens != null ? contextPercent(context.tokens, windowTokens) : null,
  };
}

/** The window panel's testid, the one place the panel is found by. */
export const PRO_BUILDER_PANEL_TESTID = 'pro-builder-window-panel';

/**
 * Where focus lands inside the window's panel: a control marked
 * `data-pro-builder-autofocus` first (a setup/error screen's own primary
 * action), then the terminal's input. The window's open rule and the publish
 * dialog's close rule both use this one order, so they cannot disagree.
 */
export function proBuilderFocusTarget(panel: Pick<ParentNode, 'querySelector'>): HTMLElement | null {
  return (
    panel.querySelector<HTMLElement>('[data-pro-builder-autofocus]') ??
    panel.querySelector<HTMLElement>('.xterm-helper-textarea')
  );
}
